import { className } from 'lib/css'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import useSeamlyEventBusContext from 'ui/hooks/event-bus-hooks'
import { ValueOf } from 'ui/utils/general-utils'
import { ariaLiveLevels } from 'ui/utils/seamly-utils'
import { SeamlyLiveRegionContext } from './seamly-live-region-context'

const SeamlyLiveRegion = ({ children }) => {
  const eventBus = useSeamlyEventBusContext()
  const timeOutHandler = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [message, setMessage] = useState<{
    messageText: string
    ariaLive: ValueOf<typeof ariaLiveLevels>
  }>()
  const isMounted = useRef(true)

  const messageSetter = useCallback(
    (state) => {
      if (isMounted.current) {
        setMessage(state)
        // @ts-ignore
        eventBus.emit('aria-live', state)
      }
    },
    [setMessage, eventBus],
  )

  useEffect(() => {
    timeOutHandler.current = null
    if (message?.messageText) {
      timeOutHandler.current = setTimeout(() => {
        messageSetter({})
        if (timeOutHandler.current) clearTimeout(timeOutHandler.current)
      }, 600) // To get it to work in VoiceOver in MacOS
    }

    return () => {
      if (timeOutHandler.current) clearTimeout(timeOutHandler.current)
    }
  }, [message, messageSetter])

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  return (
    <SeamlyLiveRegionContext.Provider value={messageSetter}>
      {children}
      <div className={className('app__live-container')}>
        <div
          aria-live="assertive"
          aria-atomic="true"
          className={className('visually-hidden')}
        >
          {message?.ariaLive === ariaLiveLevels.assertive
            ? message?.messageText
            : null}
        </div>
        <div
          aria-live="polite"
          aria-atomic="true"
          className={className('visually-hidden')}
        >
          {message?.ariaLive === ariaLiveLevels.polite
            ? message?.messageText
            : null}
        </div>
      </div>
    </SeamlyLiveRegionContext.Provider>
  )
}

export default SeamlyLiveRegion
